// Flat list of saved graphs for the sidebar (shown on /graph route).

import React, { useRef, useEffect } from 'react';
import type { SavedGraph } from '../../types';
import { relativeDate } from './FolderTree';

interface SavedGraphListProps {
  graphs: SavedGraph[];
  activeGraphId: string | null;
  renamingId: string | null;
  renameValue: string;
  onSwitchGraph: (id: string) => void;
  onStartRename: (id: string, title: string) => void;
  onRenameValueChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onGraphMenuOpen: (graphId: string, rect: DOMRect) => void;
}

export default function SavedGraphList({
  graphs,
  activeGraphId,
  renamingId,
  renameValue,
  onSwitchGraph,
  onStartRename,
  onRenameValueChange,
  onCommitRename,
  onCancelRename,
  onGraphMenuOpen,
}: SavedGraphListProps) {
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const sorted = [...graphs].sort((a, b) => b.updatedAt - a.updatedAt);

  if (sorted.length === 0) {
    return (
      <div className="malpedia-sidebar-graph-empty">
        No saved graphs yet
      </div>
    );
  }

  return (
    <div className="malpedia-sidebar-graph-list">
      {sorted.map((g) => (
        <div
          key={g.id}
          className={`malpedia-sidebar-graph-item${g.id === activeGraphId ? ' active' : ''}`}
          onClick={() => onSwitchGraph(g.id)}
          onDoubleClick={() => onStartRename(g.id, g.title)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
            onGraphMenuOpen(g.id, rect);
          }}
        >
          {renamingId === g.id ? (
            <input
              ref={renameInputRef}
              className="malpedia-sidebar-rename-input"
              value={renameValue}
              onChange={(e) => onRenameValueChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCommitRename();
                if (e.key === 'Escape') onCancelRename();
              }}
              onBlur={onCommitRename}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <span className="malpedia-sidebar-graph-title">{g.title}</span>
              <span className="malpedia-sidebar-graph-meta">
                {g.graph.nodes.length}n · {g.graph.edges.length}e · {relativeDate(g.updatedAt)}
              </span>
              <button
                className="malpedia-sidebar-session-action"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  onGraphMenuOpen(g.id, rect);
                }}
                aria-label="Graph options"
                title="More options"
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor">
                  <circle cx="7" cy="3" r="1.2" />
                  <circle cx="7" cy="7" r="1.2" />
                  <circle cx="7" cy="11" r="1.2" />
                </svg>
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
